import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { invoicesAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Receipt, CheckCircle2, Download, CreditCard, XCircle, AlertCircle, Phone, Mail } from 'lucide-react';

const STATUS_MAP = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700' },
  partially_paid: { label: 'Partially Paid', color: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  void: { label: 'Void', color: 'bg-red-100 text-red-700' },
};

const fmt$ = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoicePublicView() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try { const res = await invoicesAPI.publicView(token); setInvoice(res.data); } catch { setError('Invoice not found or link has expired.'); } finally { setLoading(false); }
  }, [token]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setPolling(false); fetchInvoice(); return; }
    try {
      const res = await invoicesAPI.paymentStatus(token, sessionId);
      if (res.data.payment_status === 'paid') { setPolling(false); fetchInvoice(); return; }
      if (res.data.status === 'expired') { setPolling(false); fetchInvoice(); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { setPolling(false); fetchInvoice(); }
  }, [token, fetchInvoice]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('canceled') === 'true' || searchParams.get('cancelled') === 'true';
    if (cancelled && invoice && invoice.status !== 'paid') { setPaymentFailed(true); }
    else if (sessionId && invoice && invoice.status !== 'paid') { setPolling(true); pollPaymentStatus(sessionId); }
  }, [searchParams, invoice?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const res = await invoicesAPI.createCheckout(token, { origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { setError(err?.response?.data?.detail || 'Could not start payment'); setPaying(false); }
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await invoicesAPI.publicPdf(token);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${invoice.invoice_number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { window.print(); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
  if (error && !invoice) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
      <Card className="max-w-md w-full rounded-2xl shadow-sm"><CardContent className="pt-8 pb-6 text-center"><Receipt className="w-12 h-12 text-stone-300 mx-auto mb-4" /><h2 className="text-lg font-bold text-stone-900 mb-1">Invoice Not Found</h2><p className="text-sm text-stone-500">{error}</p></CardContent></Card>
    </div>
  );

  const statusInfo = STATUS_MAP[invoice.status] || STATUS_MAP.draft;
  const allowedMethods = invoice.allowed_payment_methods || { card: true, cash: true, check: true, online: true };
  const canPay = ['sent', 'partially_paid'].includes(invoice.status) && invoice.balance_due > 0 && invoice.stripe_connected && allowedMethods.online !== false;
  const isPaid = invoice.status === 'paid';
  const isPartiallyPaid = invoice.balance_due > 0 && invoice.balance_due < invoice.total;
  const primaryColor = '#18181b';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="invoice-public-view">
      {/* Company Header */}
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {invoice.company_logo ? (
                <img src={invoice.company_logo} alt="" className="h-10 w-10 rounded-lg object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-stone-900 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{(invoice.company_name || 'Co')[0]}</span>
                </div>
              )}
              <div>
                <h2 className="font-bold text-stone-900 text-base">{invoice.company_name || 'Invoice'}</h2>
                <div className="flex gap-3 text-xs text-stone-400">
                  {invoice.company_phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{invoice.company_phone}</span>}
                  {invoice.company_email && <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{invoice.company_email}</span>}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadPdf} className="rounded-lg text-xs" data-testid="public-download-pdf-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6 sm:space-y-8">

        {/* Invoice Title + Status */}
        <div className="text-center space-y-2" data-testid="invoice-header">
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" data-testid="public-invoice-number">Invoice {invoice.invoice_number}</h1>
          <Badge className={`${statusInfo.color} text-xs rounded-full px-3`} data-testid="invoice-status-badge">{statusInfo.label}</Badge>
        </div>

        {/* Polling */}
        {polling && (
          <Card className="rounded-2xl border-blue-200 bg-blue-50 shadow-sm"><CardContent className="py-4 flex items-center justify-center gap-2 text-blue-700"><Loader2 className="w-5 h-5 animate-spin" /><span className="font-medium text-sm">Verifying your payment...</span></CardContent></Card>
        )}

        {/* Payment failed */}
        {paymentFailed && !isPaid && (
          <Card className="rounded-2xl border-red-200 bg-red-50 shadow-sm" data-testid="payment-failed">
            <CardContent className="py-6 flex flex-col items-center gap-2 text-red-700">
              <XCircle className="w-10 h-10 mb-1" /><span className="font-bold text-base">Payment Failed</span><span className="text-sm text-red-600">Your payment could not be processed. Please try again.</span>
              <Button className="mt-2 gap-2 rounded-xl h-11" onClick={() => { setPaymentFailed(false); handlePayNow(); }} disabled={paying} data-testid="retry-payment-btn">
                {paying ? <><Loader2 className="w-4 h-4 animate-spin" /> Retrying...</> : <><CreditCard className="w-4 h-4" /> Try Again</>}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Paid confirmation */}
        {isPaid && (
          <Card className="rounded-2xl border-green-200 bg-green-50 shadow-sm" data-testid="paid-confirmation">
            <CardContent className="py-6 flex flex-col items-center gap-1 text-green-700">
              <CheckCircle2 className="w-12 h-12 mb-2" /><span className="font-bold text-lg">Payment Received</span>
              <span className="text-sm">{invoice.paid_at ? `Paid on ${new Date(invoice.paid_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : 'Thank you for your payment'}</span>
            </CardContent>
          </Card>
        )}

        {/* Customer & Due Date */}
        <Card className="rounded-2xl shadow-sm border-stone-200">
          <CardContent className="pt-5 pb-4 flex justify-between items-start">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 mb-1">Bill To</p>
              <p className="font-semibold text-stone-900" data-testid="public-customer-name">{invoice.customer_name || 'Customer'}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 mb-1">Due Date</p>
              <p className="font-semibold text-stone-900" data-testid="public-due-date">{invoice.due_date ? new Date(invoice.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Upon receipt'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card className="rounded-2xl shadow-sm border-stone-200">
          <CardContent className="pt-5 pb-3 px-4 sm:px-6">
            <table className="w-full text-sm" data-testid="public-items-table">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className="text-left py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400">Description</th>
                  <th className="text-right py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400 w-16">Qty</th>
                  <th className="text-right py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400 w-24 hidden sm:table-cell">Unit Price</th>
                  <th className="text-right py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400 w-24">Total</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((item, idx) => (
                  <tr key={idx} className="border-b border-stone-50 last:border-0" data-testid={`public-item-${idx}`}>
                    <td className="py-3 pr-2">
                      <div className="font-medium text-stone-800">{item.name}</div>
                      {item.description && <div className="text-xs text-stone-400 mt-0.5">{item.description}</div>}
                    </td>
                    <td className="py-3 text-right text-stone-600 tabular-nums">{item.quantity}</td>
                    <td className="py-3 text-right text-stone-600 tabular-nums hidden sm:table-cell">{fmt$(item.unit_price)}</td>
                    <td className="py-3 text-right font-medium text-stone-800 tabular-nums">{fmt$(item.total_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Notes */}
        {invoice.notes && (
          <Card className="rounded-2xl shadow-sm border-stone-200">
            <CardContent className="pt-5 pb-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 mb-1.5">Notes</p>
              <p className="text-sm text-stone-600 whitespace-pre-wrap">{invoice.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Totals */}
        <Card className="rounded-2xl shadow-sm border-stone-200" data-testid="totals-card">
          <CardContent className="pt-6 pb-5 px-5 sm:px-6">
            <div className="flex justify-end">
              <div className="w-full sm:w-80 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">Subtotal</span>
                  <span className="tabular-nums font-medium text-stone-700" data-testid="public-subtotal">{fmt$(invoice.subtotal)}</span>
                </div>
                {invoice.discount_total > 0 && (
                  <>
                    <div className="border-t border-stone-100 pt-2" />
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Discount{invoice.discount_type === 'percentage' ? ` (${invoice.discount_value}%)` : ''}</span>
                      <span className="tabular-nums font-medium" data-testid="public-discount">-{fmt$(invoice.discount_total)}</span>
                    </div>
                  </>
                )}
                {(invoice.tax_amount > 0 || invoice.tax_rate) && (
                  <>
                    <div className="border-t border-stone-100 pt-2" />
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Tax{invoice.tax_rate ? ` (${invoice.tax_rate}%)` : ''}</span>
                      <span className="tabular-nums font-medium text-stone-700">{fmt$(invoice.tax_amount)}</span>
                    </div>
                  </>
                )}
                <div className="border-t-2 border-stone-200 pt-3 mt-1">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="tabular-nums" data-testid="public-total">{fmt$(invoice.total)}</span>
                  </div>
                </div>
                {isPartiallyPaid && (
                  <>
                    <div className="flex justify-between text-sm text-green-600 pt-1">
                      <span className="font-medium">Amount Paid</span>
                      <span className="tabular-nums font-medium">-{fmt$(invoice.total - invoice.balance_due)}</span>
                    </div>
                    <div className="rounded-lg px-3.5 py-2.5 bg-amber-50 border border-amber-200">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-amber-700">Balance Due</span>
                        <span className="text-base font-bold tabular-nums text-amber-700" data-testid="public-balance-due">{fmt$(invoice.balance_due)}</span>
                      </div>
                    </div>
                  </>
                )}
                {!isPartiallyPaid && !isPaid && invoice.balance_due > 0 && (
                  <div className="rounded-lg px-3.5 py-2.5 mt-1" style={{ backgroundColor: primaryColor + '08', border: `1px solid ${primaryColor}20` }}>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-stone-700">Amount Due</span>
                      <span className="text-base font-bold tabular-nums text-stone-900" data-testid="public-balance-due">{fmt$(invoice.balance_due)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pay Now CTA */}
        {canPay && !polling && !paymentFailed && (
          <div className="space-y-3" data-testid="pay-now-section">
            <Button
              size="lg"
              className="w-full h-14 text-base font-bold rounded-xl shadow-lg transition-all hover:shadow-xl active:scale-[0.98] gap-2"
              onClick={handlePayNow}
              disabled={paying}
              data-testid="pay-now-btn"
            >
              {paying ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to payment...</>
              ) : (
                <><CreditCard className="w-5 h-5" /> Pay {fmt$(invoice.balance_due)} Now</>
              )}
            </Button>
            <p className="text-[10px] text-stone-400 text-center">Secure payment powered by Stripe. Your payment info is never stored on our servers.</p>
          </div>
        )}

        {/* Contact for payment — Stripe not connected or online payment disabled */}
        {['sent', 'partially_paid'].includes(invoice.status) && invoice.balance_due > 0 && (!invoice.stripe_connected || allowedMethods.online === false) && !polling && (
          <Card className="rounded-2xl border-stone-200 shadow-sm" data-testid="payment-unavailable">
            <CardContent className="py-6 text-center space-y-3">
              <AlertCircle className="w-7 h-7 text-stone-400 mx-auto" />
              <div>
                <p className="text-sm text-stone-700 font-medium">Online payments are not enabled for this invoice.</p>
                <p className="text-xs text-stone-400 mt-0.5">Please contact us to arrange payment.</p>
              </div>
              {(invoice.company_phone || invoice.company_email) && (
                <div className="flex items-center justify-center gap-3 pt-1">
                  {invoice.company_phone && (
                    <a href={`tel:${invoice.company_phone.replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-medium transition-colors" data-testid="contact-phone-link">
                      <Phone className="w-3.5 h-3.5" /> Call {invoice.company_phone}
                    </a>
                  )}
                  {invoice.company_email && (
                    <a href={`mailto:${invoice.company_email}?subject=Invoice%20%23${invoice.invoice_number}%20Payment`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-medium transition-colors" data-testid="contact-email-link">
                      <Mail className="w-3.5 h-3.5" /> Email Us
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-100 mt-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 text-center">
          <p className="text-[10px] text-stone-400">
            {invoice.company_name && <>{invoice.company_name} &middot; </>}
            Invoice generated securely
          </p>
        </div>
      </footer>
    </div>
  );
}
